import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  ChevronRight, 
  ChevronDown,
  Maximize2,
  Minimize2,
  Save,
  X,
  FolderOpen,
  Folder,
  AlertCircle,
  GripVertical,
  Loader,
  Package,
  List,
  ArrowUpCircle,
  FileText
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc,
  getDoc,
  onSnapshot, 
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useClient } from '../../contexts/ClientContext';
import MarcasRulesModal from './MarcasRulesModal';
import FilteredProductsModal from '../FilteredProductsModal';
import api from '../../utils/api';

interface MarcaNode {
  id: string;
  name: string;
  parentId: string | null;
  children?: MarcaNode[];
  level: number;
  order: number;
  productCount?: number;
  sitesCount?: number;
  categories?: string[];
}

interface FlatNode extends MarcaNode {
  path: string[];
}

interface DropIndicator {
  targetId: string;
  position: 'before' | 'after' | 'child';
}

// Componente para cada nodo del árbol
const TreeNode: React.FC<{
  node: MarcaNode;
  expanded: boolean;
  onToggle: () => void;
  onEdit: (node: MarcaNode) => void;
  onDelete: (node: MarcaNode) => void;
  onAddChild: (node: MarcaNode) => void;
  onViewCategories: (node: MarcaNode) => void;
  onPromoteToRoot: (node: MarcaNode) => void;
  searchTerm: string;
  isHighlighted: boolean;
  isDragging?: boolean;
  dropIndicator?: DropIndicator | null;
  productCounts: { [key: string]: number };
  setSelectedBrandForProducts: (name: string) => void;
  setShowProductsModal: (show: boolean) => void;
}> = ({ 
  node, 
  expanded, 
  onToggle, 
  onEdit, 
  onDelete, 
  onAddChild,
  onViewCategories,
  onPromoteToRoot,
  searchTerm, 
  isHighlighted,
  isDragging = false,
  dropIndicator = null,
  productCounts,
  setSelectedBrandForProducts,
  setShowProductsModal
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: node.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(node.name);
  const [localExpanded, setLocalExpanded] = useState(expanded);

  useEffect(() => {
    setLocalExpanded(expanded);
  }, [expanded]);

  const handleSave = () => {
    if (editName.trim() && editName !== node.name) {
      onEdit({ ...node, name: editName.trim() });
    }
    setIsEditing(false);
    setEditName(node.name);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName(node.name);
  };

  const hasChildren = node.children && node.children.length > 0;
  
  // Determinar si el nodo está resaltado
  const isSearchMatch = searchTerm && 
    node.name.toLowerCase().includes(searchTerm.toLowerCase());

  // Determinar el indicador de drop
  const showDropIndicator = dropIndicator && dropIndicator.targetId === node.id;
  const dropPosition = dropIndicator?.position;

  return (
    <div style={style} ref={setNodeRef}>
      {/* Indicador de drop antes */}
      {showDropIndicator && dropPosition === 'before' && (
        <div className="h-1 bg-blue-500 my-1 ml-6 rounded-full shadow-sm"></div>
      )}
      
      <div
        className={`
          group flex items-center py-1.5 px-2 rounded-lg hover:bg-gray-50 relative
          ${isHighlighted ? 'bg-yellow-100' : ''}
          ${isSearchMatch ? 'bg-blue-50 ring-1 ring-blue-300' : ''}
          ${showDropIndicator && dropPosition === 'child' ? 'ring-2 ring-blue-400 bg-blue-50' : ''}
        `}
        style={{ paddingLeft: `${(node.level * 24) + 8}px` }}
      >
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab hover:bg-gray-200 rounded p-1 mr-2 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-4 h-4 text-gray-500" />
        </div>

        {/* Expand/Collapse button */}
        <button
          onClick={onToggle}
          className={`p-0.5 rounded hover:bg-gray-200 mr-2 ${!hasChildren ? 'invisible' : ''}`}
        >
          {localExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {/* Icon */}
        <div className="mr-2">
          {hasChildren ? (
            localExpanded ? (
              <FolderOpen className="w-5 h-5 text-blue-600" />
            ) : (
              <Folder className="w-5 h-5 text-blue-600" />
            )
          ) : (
            <Package className="w-5 h-5 text-gray-500" />
          )}
        </div>

        {/* Name */}
        <div className="flex-1 flex items-center">
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
              className="px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <span className="font-medium text-gray-700 flex items-center gap-2">
              {node.name}
              {(() => {
                const nodeNameTrimmed = node.name.trim();
                const count = productCounts[nodeNameTrimmed] || productCounts[node.name] || 0;
                return count > 0;
              })() && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBrandForProducts(node.name);
                    setShowProductsModal(true);
                  }}
                  className="inline-flex items-center px-2 py-0.5 text-xs rounded-full transition-colors hover:opacity-80 cursor-pointer" 
                  style={{
                    backgroundColor: 'rgba(219, 39, 119, 0.1)',
                    color: 'rgb(219, 39, 119)',
                    border: '1px solid rgba(219, 39, 119, 0.3)'
                  }}
                  title={`${productCounts[node.name.trim()] || productCounts[node.name] || 0} productos en la toma actual - Click para ver`}>
                  {productCounts[node.name.trim()] || productCounts[node.name] || 0}
                </button>
              )}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onViewCategories(node)}
            className="p-1 text-purple-600 hover:bg-purple-50 rounded relative"
            title="Ver categorías"
          >
            <List className="w-4 h-4" />
            {node.categories && node.categories.length > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-600 rounded-full"></span>
            )}
          </button>
          {/* Botón para promover a marca principal (solo para submarcas) */}
          {node.parentId && (
            <button
              onClick={() => onPromoteToRoot(node)}
              className="p-1 text-orange-600 hover:bg-orange-50 rounded"
              title="Convertir en marca principal"
            >
              <ArrowUpCircle className="w-4 h-4" />
            </button>
          )}
          {node.level < 2 && (
            <button
              onClick={() => onAddChild(node)}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Agregar submarca"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="Editar nombre"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Indicador de drop después */}
      {showDropIndicator && dropPosition === 'after' && (
        <div className="h-1 bg-blue-500 my-1 ml-6 rounded-full shadow-sm"></div>
      )}
    </div>
  );
};

const Marcas: React.FC = () => {
  const { selectedClient } = useClient();
  const [marcas, setMarcas] = useState<MarcaNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [newNodeName, setNewNodeName] = useState('');
  const [showNewRootForm, setShowNewRootForm] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [rulesCount, setRulesCount] = useState(0);
  const [productCounts, setProductCounts] = useState<{ [key: string]: number }>({});
  const [currentTableName, setCurrentTableName] = useState<string | null>(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedBrandForProducts, setSelectedBrandForProducts] = useState<string>('');

  // Obtener referencia a la colección de marcas del cliente
  const getMarcasCollection = useCallback(() => {
    if (!selectedClient?.clientId || selectedClient.clientId === 'admin-mode') {
      return null;
    }
    return collection(db, 'catalogado', selectedClient.clientId, 'marcas');
  }, [selectedClient]);

  // Función para obtener el conteo de productos
  const fetchProductCounts = useCallback(async () => {
    if (!selectedClient || !currentTableName) return;
    
    try {
      const response = await api.get(
        `/api/tables/${currentTableName}/catalogado-counts`,
        {
          headers: {
            'X-Client-Id': selectedClient.clientId
          }
        }
      );
      
      if (response.data && response.data.brands) {
        // Crear un mapa case-insensitive de los conteos
        const caseInsensitiveMap: { [key: string]: number } = {};
        Object.entries(response.data.brands).forEach(([key, value]) => {
          // Guardar con múltiples variaciones para mejor matching
          caseInsensitiveMap[key] = value as number; // Original
          caseInsensitiveMap[key.trim()] = value as number; // Trimmed
          caseInsensitiveMap[key.toLowerCase()] = value as number; // Lowercase
          caseInsensitiveMap[key.trim().toLowerCase()] = value as number; // Trimmed + lowercase
        });
        
        
        // Crear objeto final de conteos buscando coincidencias flexibles
        const finalCounts: { [key: string]: number } = {};
        
        const assignCounts = (nodes: MarcaNode[]) => {
          nodes.forEach((node: MarcaNode) => {
            const nodeName = node.name;
            
            // Buscar coincidencia en el mapa con diferentes variaciones
            const count = 
              caseInsensitiveMap[nodeName] || 
              caseInsensitiveMap[nodeName.trim()] ||
              caseInsensitiveMap[nodeName.toLowerCase()] ||
              caseInsensitiveMap[nodeName.trim().toLowerCase()] ||
              0;
            
            if (count > 0) {
              finalCounts[nodeName] = count;
            }
            
            // Procesar hijos
            if (node.children && node.children.length > 0) {
              assignCounts(node.children);
            }
          });
        };
        
        // Aplicar asignación
        assignCounts(marcas);
        
        setProductCounts(finalCounts);
      }
    } catch (error) {
      console.error('Error fetching product counts:', error);
    }
  }, [selectedClient, currentTableName]);

  // Obtener nombre de la tabla actual
  useEffect(() => {
    const fetchCurrentTable = async () => {
      if (!selectedClient) return;
      
      try {
        const response = await api.get(
          `/api/tables`,
          {
            headers: {
              'X-Client-Id': selectedClient.clientId
            }
          }
        );
        
        if (response.data && response.data.length > 0) {
          // La respuesta tiene formato: [{ "Tables_in_xxx": "toma_14" }]
          const tables = response.data.map((item: any) => {
            const key = Object.keys(item)[0];
            return item[key];
          });
          
          // Tomar la tabla más reciente
          const sortedTables = tables.sort((a: string, b: string) => {
            const numA = parseInt(a.replace('toma_', ''));
            const numB = parseInt(b.replace('toma_', ''));
            return numB - numA;
          });
          
          const tableName = sortedTables[0];
          setCurrentTableName(tableName);
        }
      } catch (error) {
        console.error('Error fetching tables:', error);
      }
    };
    
    fetchCurrentTable();
  }, [selectedClient]);

  // Obtener conteos cuando cambie la tabla
  useEffect(() => {
    if (currentTableName) {
      fetchProductCounts();
    }
  }, [currentTableName, fetchProductCounts]);

  // Cargar marcas desde Firebase
  useEffect(() => {
    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(marcasCollection, (snapshot) => {
      const marcasMap = new Map<string, MarcaNode>();

      // Primero, crear todos los nodos
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        const node: MarcaNode = {
          id: doc.id,
          name: data.name || '', 
          parentId: data.parentId || null,
          level: data.level || 0,
          order: data.order || 0,
          productCount: data.productCount || 0,
          sitesCount: data.sitesCount || 0,
          categories: data.categories || [],
          children: []
        };
        
        marcasMap.set(node.id, node);
      });

      // Construir la estructura de árbol
      const rootNodes: MarcaNode[] = [];
      marcasMap.forEach((node) => {
        if (node.parentId) {
          const parent = marcasMap.get(node.parentId);
          if (parent) {
            if (!parent.children) parent.children = [];
            parent.children.push(node);
          } else {
            console.warn(`⚠️ Marca huérfana: "${node.name}" tiene parentId "${node.parentId}" pero el padre no existe`);
            // Agregar como nodo raíz si el padre no existe
            rootNodes.push(node);
          }
        } else {
          rootNodes.push(node);
        }
      });
      

      // Ordenar nodos alfabéticamente por nombre
      const sortNodes = (nodes: MarcaNode[]) => {
        nodes.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        nodes.forEach((node) => {
          if (node.children && node.children.length > 0) {
            sortNodes(node.children);
          }
        });
      };
      sortNodes(rootNodes);

      setMarcas(rootNodes);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [getMarcasCollection]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Aplanar el árbol para la búsqueda y el drag & drop
  const flattenTree = useCallback((nodes: MarcaNode[], path: string[] = []): FlatNode[] => {
    return nodes.reduce((acc: FlatNode[], node) => {
      const currentPath = [...path, node.name];
      const flatNode: FlatNode = { ...node, path: currentPath };
      
      if (expandedNodes.has(node.id) && node.children) {
        return [...acc, flatNode, ...flattenTree(node.children, currentPath)];
      }
      
      return [...acc, flatNode];
    }, []);
  }, [expandedNodes]);

  // Buscar nodos que coinciden con el término de búsqueda
  const searchInTree = useCallback((nodes: MarcaNode[], term: string): Set<string> => {
    const matchingIds = new Set<string>();
    
    if (!term) return matchingIds;
    
    const searchRecursive = (nodeList: MarcaNode[], parentMatches: boolean = false) => {
      nodeList.forEach(node => {
        const matches = node.name.toLowerCase().includes(term.toLowerCase());
        
        if (matches || parentMatches) {
          matchingIds.add(node.id);
          // Marcar todos los padres para expandir
          let currentId = node.parentId;
          while (currentId) {
            matchingIds.add(currentId);
            const parent = findNodeInList(nodes, currentId);
            if (parent) {
              currentId = parent.parentId;
            } else {
              break;
            }
          }
        }
        
        if (node.children && node.children.length > 0) {
          searchRecursive(node.children, matches);
        }
      });
    };
    
    searchRecursive(nodes);
    
    // Expandir nodos encontrados
    if (matchingIds.size > 0) {
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        matchingIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
    
    return matchingIds;
  }, []);

  const highlightedNodes = useMemo(() => {
    return searchInTree(marcas, searchTerm);
  }, [marcas, searchTerm, searchInTree]);

  // Verificar si un nombre ya existe en el árbol
  const isNameUnique = (name: string, excludeId?: string): boolean => {
    const checkRecursive = (nodes: MarcaNode[]): boolean => {
      for (const node of nodes) {
        if (node.id !== excludeId && node.name.toLowerCase() === name.toLowerCase()) {
          return false;
        }
        if (node.children && !checkRecursive(node.children)) {
          return false;
        }
      }
      return true;
    };
    
    return checkRecursive(marcas);
  };

  // Encontrar un nodo por ID
  const findNodeById = (nodes: MarcaNode[], id: string): MarcaNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Encontrar un nodo por ID en una lista plana
  const findNodeInList = (nodes: MarcaNode[], id: string): MarcaNode | null => {
    const searchInNodes = (nodeList: MarcaNode[]): MarcaNode | null => {
      for (const node of nodeList) {
        if (node.id === id) return node;
        if (node.children) {
          const found = searchInNodes(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return searchInNodes(nodes);
  };

  // Expandir todos los nodos
  const expandAll = () => {
    const getAllIds = (nodes: MarcaNode[]): string[] => {
      return nodes.reduce((acc: string[], node) => {
        const childIds = node.children ? getAllIds(node.children) : [];
        return [...acc, node.id, ...childIds];
      }, []);
    };
    
    const allIds = getAllIds(marcas);
    const newSet = new Set<string>();
    allIds.forEach(id => newSet.add(id));
    setExpandedNodes(newSet);
  };

  // Colapsar todos los nodos
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Toggle expandir/colapsar un nodo
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Agregar nueva marca raíz (soporta carga masiva separada por comas)
  const addRootMarca = async () => {
    if (!newNodeName.trim()) {
      setError('El nombre no puede estar vacío');
      return;
    }

    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    setSaving(true);
    setError(null);
    
    try {
      // Detectar si hay múltiples marcas separadas por comas
      const marcasInput = newNodeName.includes(',') 
        ? newNodeName.split(',').map(m => m.trim()).filter(m => m.length > 0)
        : [newNodeName.trim()];

      // Validar que todos los nombres sean únicos
      const duplicates: string[] = [];
      const validMarcas: string[] = [];
      
      for (const marcaName of marcasInput) {
        if (isNameUnique(marcaName)) {
          validMarcas.push(marcaName);
        } else {
          duplicates.push(marcaName);
        }
      }

      if (duplicates.length > 0) {
        setError(`Ya existen marcas con estos nombres: ${duplicates.join(', ')}`);
        if (validMarcas.length === 0) {
          setSaving(false);
          return;
        }
      }

      // Usar batch para crear múltiples marcas de manera eficiente
      const batch = writeBatch(db);
      let createdCount = 0;
      
      for (const marcaName of validMarcas) {
        const timestamp = Date.now() + createdCount; // Agregar offset para IDs únicos
        const cleanName = marcaName.toLowerCase().replace(/\s+/g, '_');
        const newId = `marca_${cleanName}_${timestamp}`;
        
        const newMarca = {
          name: marcaName,
          parentId: null,
          level: 0,
          order: marcas.length + createdCount,
          productCount: 0,
          sitesCount: 0,
          categories: [],
          createdAt: Timestamp.now()
        };
        
        batch.set(doc(marcasCollection, newId), newMarca);
        createdCount++;
      }
      
      await batch.commit();
      
      // Mensaje de éxito
      if (createdCount > 1) {
        setSuccessMessage(`✅ Se crearon ${createdCount} marcas exitosamente`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (createdCount === 1) {
        setSuccessMessage(`✅ Marca "${validMarcas[0]}" creada exitosamente`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
      
      setNewNodeName('');
      setShowNewRootForm(false);
    } catch (error) {
      console.error('Error adding marcas:', error);
      setError('Error al agregar las marcas');
    } finally {
      setSaving(false);
    }
  };

  // Agregar submarca
  const addChildMarca = async (parent: MarcaNode) => {
    const name = prompt('Nombre de la submarca:');
    if (!name?.trim()) return;

    if (!isNameUnique(name.trim())) {
      alert('Ya existe una marca con ese nombre');
      return;
    }

    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    setSaving(true);
    try {
      // Generar un ID único basado en el nombre pero mantener el nombre original
      const timestamp = Date.now();
      const cleanName = name.trim().toLowerCase().replace(/\s+/g, '_');
      const newId = `marca_${cleanName}_${timestamp}`;
      
      const newMarca = {
        name: name.trim(), // Guardar el nombre exacto como el usuario lo escribió
        parentId: parent.id,
        level: parent.level + 1,
        order: parent.children ? parent.children.length : 0,
        productCount: 0,
        sitesCount: 0,
        categories: [],
        createdAt: Timestamp.now()
      };
      
      await setDoc(doc(marcasCollection, newId), newMarca);

      // Expandir el padre para mostrar el nuevo hijo
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        newSet.add(parent.id);
        return newSet;
      });
    } catch (error) {
      console.error('Error adding child marca:', error);
      alert('Error al agregar la submarca');
    } finally {
      setSaving(false);
    }
  };

  // Editar marca
  const editMarca = async (node: MarcaNode) => {
    if (!isNameUnique(node.name, node.id)) {
      setError('Ya existe una marca con ese nombre');
      return;
    }

    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    setSaving(true);
    try {
      await setDoc(doc(marcasCollection, node.id), {
        name: node.name,
        parentId: node.parentId,
        level: node.level,
        order: node.order,
        productCount: node.productCount || 0,
        sitesCount: node.sitesCount || 0,
        categories: node.categories || [],
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating marca:', error);
      setError('Error al actualizar la marca');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar marca
  const deleteMarca = async (node: MarcaNode) => {
    if (node.children && node.children.length > 0) {
      if (!window.confirm(`¿Eliminar "${node.name}" y todas sus submarcas?`)) {
        return;
      }
    } else {
      if (!window.confirm(`¿Eliminar la marca "${node.name}"?`)) {
        return;
      }
    }

    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    setSaving(true);
    try {
      // Función recursiva para eliminar nodo y sus hijos
      const deleteRecursive = async (nodeToDelete: MarcaNode) => {
        if (nodeToDelete.children) {
          for (const child of nodeToDelete.children) {
            await deleteRecursive(child);
          }
        }
        await deleteDoc(doc(marcasCollection, nodeToDelete.id));
      };

      await deleteRecursive(node);
    } catch (error) {
      console.error('Error deleting marca:', error);
      alert('Error al eliminar la marca');
    } finally {
      setSaving(false);
    }
  };

  // Ver categorías de la marca
  const viewCategories = (node: MarcaNode) => {
    // TODO: Implementar modal para ver categorías de la marca
    alert(`Categorías de ${node.name}: ${node.categories?.join(', ') || 'Sin categorías'}`);
  };

  // Promover submarca a marca principal
  const promoteToRoot = async (node: MarcaNode) => {
    if (!node.parentId) {
      setError('Esta marca ya es principal');
      return;
    }

    const confirmMessage = node.children && node.children.length > 0
      ? `¿Convertir "${node.name}" y sus ${node.children.length} submarca(s) en marca principal?`
      : `¿Convertir "${node.name}" en marca principal?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    setSaving(true);
    try {
      const batch = writeBatch(db);
      
      // Actualizar el nodo para ser de nivel 0
      batch.update(doc(marcasCollection, node.id), {
        parentId: null,
        level: 0,
        updatedAt: Timestamp.now()
      });

      // Si tiene hijos, actualizar sus niveles
      if (node.children && node.children.length > 0) {
        const updateChildrenLevels = (children: MarcaNode[], parentLevel: number) => {
          children.forEach(child => {
            batch.update(doc(marcasCollection, child.id), {
              level: parentLevel + 1,
              updatedAt: Timestamp.now()
            });
            if (child.children) {
              updateChildrenLevels(child.children, parentLevel + 1);
            }
          });
        };
        updateChildrenLevels(node.children, 0);
      }

      await batch.commit();
      
      setSuccessMessage(`✅ "${node.name}" ahora es una marca principal`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error promoting marca:', error);
      setError('Error al promover la marca');
    } finally {
      setSaving(false);
    }
  };

  // Handlers de Drag & Drop
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active, delta } = event;
    
    if (!over || over.id === active.id) {
      setDropIndicator(null);
      return;
    }

    // Determinar la posición del drop
    const overNode = findNodeById(marcas, over.id as string);
    const activeNode = findNodeById(marcas, active.id as string);
    if (!overNode || !activeNode) return;

    let position: 'before' | 'after' | 'child' = 'after';
    
    // Si se presiona Shift, siempre insertar al mismo nivel (before/after)
    const shiftPressed = event.activatorEvent && 
      'shiftKey' in event.activatorEvent && event.activatorEvent.shiftKey;
    
    if (!shiftPressed) {
      // Solo permitir como hijo si el nodo objetivo tiene nivel < 2
      if (overNode.level < 2) {
        // Por defecto, insertar como hijo
        position = 'child';
        
        // Si el delta Y es negativo (moviendo hacia arriba), insertar antes
        if (delta && delta.y < -10) {
          position = 'before';
        }
        // Si el delta Y es muy positivo (moviendo hacia abajo), insertar después
        else if (delta && delta.y > 20) {
          position = 'after';
        }
      } else {
        // Si el nodo tiene nivel máximo, solo permitir before/after
        position = delta && delta.y < 0 ? 'before' : 'after';
      }
    } else {
      // Con Shift presionado, determinar si es before o after basado en la posición
      position = delta && delta.y < 0 ? 'before' : 'after';
    }

    setDropIndicator({
      targetId: over.id as string,
      position
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setDropIndicator(null);
    
    const { active, over } = event;
    
    if (!over || over.id === active.id) return;
    
    const marcasCollection = getMarcasCollection();
    if (!marcasCollection) return;

    const draggedNode = findNodeById(marcas, active.id as string);
    const targetNode = findNodeById(marcas, over.id as string);
    
    if (!draggedNode || !targetNode) return;

    setSaving(true);
    try {
      // Actualizar la estructura en Firebase
      const batch = writeBatch(db);
      
      // Determinar el nuevo parentId y level basado en la posición del drop
      let newParentId: string | null = null;
      let newLevel = 0;
      
      if (dropIndicator?.position === 'child' && targetNode.level < 2) {
        // Mover como hijo del nodo objetivo
        newParentId = targetNode.id;
        newLevel = targetNode.level + 1;
      } else if (dropIndicator?.position === 'before' || dropIndicator?.position === 'after') {
        // Mover al mismo nivel que el nodo objetivo
        newParentId = targetNode.parentId;
        newLevel = targetNode.level;
      } else {
        // Por defecto, mover al mismo nivel
        newParentId = targetNode.parentId;
        newLevel = targetNode.level;
      }

      // Actualizar el nodo movido
      batch.update(doc(marcasCollection, draggedNode.id), {
        parentId: newParentId,
        level: newLevel,
        updatedAt: Timestamp.now()
      });

      // Si el nodo tiene hijos, actualizar sus niveles
      if (draggedNode.children) {
        const updateChildrenLevels = (children: MarcaNode[], parentLevel: number) => {
          children.forEach(child => {
            batch.update(doc(marcasCollection, child.id), {
              level: parentLevel + 1,
              updatedAt: Timestamp.now()
            });
            if (child.children) {
              updateChildrenLevels(child.children, parentLevel + 1);
            }
          });
        };
        updateChildrenLevels(draggedNode.children, newLevel);
      }

      await batch.commit();
    } catch (error) {
      console.error('Error moving marca:', error);
      alert('Error al mover la marca');
    } finally {
      setSaving(false);
    }
  };

  // Obtener items ordenados para el SortableContext
  const sortableItems = useMemo(() => {
    const flatNodes = flattenTree(marcas);
    return flatNodes.map(node => node.id);
  }, [marcas, flattenTree]);

  // Función para obtener todas las marcas en formato plano
  const getAllMarcasFlat = useCallback(() => {
    const flatList: Array<{ id: string; name: string }> = [];
    const addToFlatList = (nodes: MarcaNode[]) => {
      nodes.forEach(node => {
        flatList.push({ id: node.id, name: node.name });
        if (node.children) {
          addToFlatList(node.children);
        }
      });
    };
    addToFlatList(marcas);
    return flatList;
  }, [marcas]);

  // Función para cargar el conteo de reglas de marcas
  const loadRulesCount = useCallback(async () => {
    if (!selectedClient?.clientId) return;
    
    try {
      const rulesDoc = await getDoc(
        doc(db, 'catalogado', selectedClient.clientId, 'marcas', 'rules')
      );
      if (rulesDoc.exists()) {
        const rules = rulesDoc.data().rules || [];
        setRulesCount(rules.length);
      } else {
        setRulesCount(0);
      }
    } catch (error) {
      console.error('Error loading marca rules count:', error);
      setRulesCount(0);
    }
  }, [selectedClient]);

  // Cargar el conteo de reglas al cargar el componente
  useEffect(() => {
    loadRulesCount();
  }, [loadRulesCount]);

  // Renderizar árbol recursivamente
  const renderTree = (nodes: MarcaNode[]) => {
    return nodes.map(node => (
      <div key={node.id}>
        <TreeNode
          node={node}
          expanded={expandedNodes.has(node.id)}
          onToggle={() => toggleNode(node.id)}
          onEdit={editMarca}
          onDelete={deleteMarca}
          onAddChild={addChildMarca}
          onViewCategories={viewCategories}
          onPromoteToRoot={promoteToRoot}
          searchTerm={searchTerm}
          isHighlighted={highlightedNodes.has(node.id)}
          isDragging={activeId === node.id}
          dropIndicator={dropIndicator}
          productCounts={productCounts}
          setSelectedBrandForProducts={setSelectedBrandForProducts}
          setShowProductsModal={setShowProductsModal}
        />
        {expandedNodes.has(node.id) && node.children && node.children.length > 0 && (
          <div>
            {renderTree(node.children)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="space-y-4">
      {/* Header con título y acciones */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Gestión de Marcas</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            title="Expandir todo"
          >
            <Maximize2 className="w-4 h-4" />
            <span>Expandir</span>
          </button>
          <button
            onClick={collapseAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            title="Colapsar todo"
          >
            <Minimize2 className="w-4 h-4" />
            <span>Colapsar</span>
          </button>
        </div>
      </div>

      {/* Barra de búsqueda y acciones */}
      <div className="flex gap-3 items-center">
        {/* Búsqueda */}
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar marcas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="flex gap-2 ml-auto">
          {/* Botón de Reglas */}
          <button
          onClick={() => setShowRulesModal(true)}
          className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          style={{
            backgroundColor: 'rgba(219, 39, 119, 0.1)',
            color: 'rgb(219, 39, 119)',
            border: '1px solid rgba(219, 39, 119, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(219, 39, 119, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(219, 39, 119, 0.1)';
          }}
        >
          <FileText className="w-4 h-4" />
          Reglas
          {rulesCount > 0 && (
            <span 
              className="rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold"
              style={{ 
                color: 'rgb(219, 39, 119)',
                backgroundColor: 'transparent',
                border: '1.5px solid rgb(219, 39, 119)'
              }}
            >
              {rulesCount}
            </span>
          )}
          </button>
          
          {/* Botón Nueva Marca */}
          <button
            onClick={() => setShowNewRootForm(true)}
            className="px-4 py-2 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nueva Marca
          </button>
        </div>
      </div>

      {/* Formulario para nueva marca raíz */}
      {showNewRootForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-2">Agregar nueva marca principal</h3>
          <p className="text-xs text-gray-600 mb-3">
            💡 Puedes ingresar múltiples marcas separadas por comas. Ej: Nike, Adidas, Puma
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Nombre de la marca o varias separadas por comas"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newNodeName}
              onChange={(e) => {
                setNewNodeName(e.target.value);
                setError(null); // Limpiar error al escribir
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addRootMarca();
                if (e.key === 'Escape') {
                  setShowNewRootForm(false);
                  setNewNodeName('');
                  setError(null);
                }
              }}
              autoFocus
              disabled={saving}
            />
            <button
              onClick={addRootMarca}
              className="btn btn-primary flex items-center gap-2"
              disabled={saving || !newNodeName.trim()}
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {newNodeName.includes(',') ? 'Guardar Marcas' : 'Guardar'}
            </button>
            <button
              onClick={() => {
                setShowNewRootForm(false);
                setNewNodeName('');
                setError(null);
              }}
              className="btn btn-default"
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Mostrar preview de las marcas que se van a crear */}
          {newNodeName.includes(',') && (
            <div className="mt-2 p-2 bg-white rounded border border-blue-200">
              <p className="text-xs text-gray-600 mb-1">Se crearán las siguientes marcas:</p>
              <div className="flex flex-wrap gap-1">
                {newNodeName.split(',').map((m, idx) => {
                  const trimmed = m.trim();
                  if (!trimmed) return null;
                  const isValid = isNameUnique(trimmed);
                  return (
                    <span 
                      key={idx} 
                      className={`px-2 py-1 text-xs rounded ${
                        isValid 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700 line-through'
                      }`}
                    >
                      {trimmed}
                    </span>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          )}
          {error && (
            <div className="mt-2 flex items-center text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </div>
          )}
        </div>
      )}

      {/* Estado de guardado */}
      {saving && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
          <Loader className="w-4 h-4 animate-spin text-blue-600 mr-2" />
          <span className="text-sm text-blue-700">Guardando cambios...</span>
        </div>
      )}

      {/* Mensaje de éxito */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
          <span className="text-sm text-green-700">{successMessage}</span>
        </div>
      )}

      {/* Árbol de marcas */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        {loading ? (
          <div className="text-center py-8">
            <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Cargando marcas...</p>
          </div>
        ) : marcas.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No hay marcas creadas</p>
            <button
              onClick={() => setShowNewRootForm(true)}
              className="mt-3 text-blue-600 hover:text-blue-700"
            >
              Crear primera marca
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1">
                {renderTree(marcas)}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <div className="bg-white shadow-lg rounded p-2 opacity-80">
                  {findNodeById(marcas, activeId)?.name}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Modal de Reglas de Marcas */}
      {showRulesModal && selectedClient && (
        <MarcasRulesModal
          isOpen={showRulesModal}
          onClose={() => {
            setShowRulesModal(false);
            // Recargar conteo de reglas
            loadRulesCount();
          }}
          clientId={selectedClient.clientId}
          marcas={getAllMarcasFlat()}
        />
      )}

      {/* Modal de Productos Filtrados */}
      {showProductsModal && currentTableName && (
        <FilteredProductsModal
          isOpen={showProductsModal}
          onClose={() => {
            setShowProductsModal(false);
            setSelectedBrandForProducts('');
          }}
          tableName={currentTableName}
          filterType="brand"
          filterValue={selectedBrandForProducts}
          title={`Productos de ${selectedBrandForProducts}`}
        />
      )}
    </div>
  );
};

export default Marcas;